import { Alert, SimpleGrid, Stack } from '@mantine/core';
import { useMemo, useState } from 'react';
import { dashboardApi } from '../../../api/dashboard';
import { useGroup } from '../../../contexts/GroupContext';
import { useGroupQuery } from '../../../hooks/useGroupQuery';
import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { toDemographicCounts } from '../demographics';
import { collectGoalsByMetric } from '../goalsByMetric';
import { PerformanceCardSkeleton } from '../PerformanceCardSkeleton';
import { MAX_QUARTERS_CHART } from '../constants';
import type { TuAswpData } from './types';
import { DeclaredCard } from './cards/DeclaredCard';
import { GraduatesCard } from './cards/GraduatesCard';
import { HighSchoolCard } from './cards/HighSchoolCard';
import { ParticipationCompletionCard } from './cards/ParticipationCompletionCard';
import { TeachersCard } from './cards/TeachersCard';

type Mode = 'race' | 'gender';

const asNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

function mapApiToTuAswpData(data: DashboardMetricsResponse): TuAswpData | null {
  const section = data.sections.find((entry) => entry.sectionId === 'tuAswp');
  if (!section) return null;
  const byId = new Map(section.metrics.map((metric) => [metric.metricId, metric]));

  const emptyData: TuAswpData = {
    declared: [],
    declaredAllTimeTotal: 0,
    declaredAllTimeByRace: {},
    declaredAllTimeByGender: {},
    graduates: [],
    graduatedAllTimeTotal: 0,
    graduatedAllTimeByRace: {},
    graduatedAllTimeByGender: {},
    highSchool: [],
    teachers: [],
    goalsByMetric: {},
  };

  const declared = byId.get('tuAswp.declared');
  const declaredByBreakdown = byId.get('tuAswp.declaredByRaceGender');
  if (declared) {
    let runningDeclared = 0;
    emptyData.declared = (declared.series ?? [])
      .map((point) => {
      runningDeclared += asNumber(point.declaredNew);
      const breakdown = declaredByBreakdown?.series.find((entry) => String(entry.quarter) === String(point.quarter));
      return {
        quarter: String(point.quarter),
        declaredNew: asNumber(point.declaredNew),
        declaredTotal: runningDeclared,
        declaredNewByRace: toDemographicCounts(breakdown?.declaredNewByRace),
        declaredNewByGender: toDemographicCounts(breakdown?.declaredNewByGender),
        qoq: (point.qoq ?? {}) as Record<string, unknown>,
        yoy: (point.yoy ?? {}) as Record<string, unknown>,
      };
    })
      .slice(-MAX_QUARTERS_CHART);
    emptyData.declaredAllTimeTotal = asNumber(declared.allTime?.declaredNew);
    emptyData.declaredAllTimeByRace = toDemographicCounts(declaredByBreakdown?.allTime?.declaredNewByRace);
    emptyData.declaredAllTimeByGender = toDemographicCounts(declaredByBreakdown?.allTime?.declaredNewByGender);
  }

  const graduates = byId.get('tuAswp.graduates');
  const graduatesByBreakdown = byId.get('tuAswp.graduatesByRaceGender');
  if (graduates) {
    let runningGraduates = 0;
    emptyData.graduates = (graduates.series ?? [])
      .map((point) => {
      runningGraduates += asNumber(point.graduated);
      const breakdown = graduatesByBreakdown?.series.find((entry) => String(entry.quarter) === String(point.quarter));
      return {
        quarter: String(point.quarter),
        graduated: asNumber(point.graduated),
        graduatedTotal: runningGraduates,
        graduatedByRace: toDemographicCounts(breakdown?.graduatedByRace),
        graduatedByGender: toDemographicCounts(breakdown?.graduatedByGender),
        qoq: (point.qoq ?? {}) as Record<string, unknown>,
        yoy: (point.yoy ?? {}) as Record<string, unknown>,
      };
    })
      .slice(-MAX_QUARTERS_CHART);
    emptyData.graduatedAllTimeTotal = asNumber(graduates.allTime?.graduated);
    emptyData.graduatedAllTimeByRace = toDemographicCounts(graduatesByBreakdown?.allTime?.graduatedByRace);
    emptyData.graduatedAllTimeByGender = toDemographicCounts(graduatesByBreakdown?.allTime?.graduatedByGender);
  }

  const highSchool = byId.get('tuAswp.highSchoolEngaged');
  if (highSchool) {
    emptyData.highSchool = (highSchool.series ?? []).slice(-MAX_QUARTERS_CHART).map((point) => ({
      quarter: String(point.quarter),
      highSchoolEngaged: asNumber(point.highSchoolEngaged),
      qoq: (point.qoq ?? {}) as Record<string, unknown>,
      yoy: (point.yoy ?? {}) as Record<string, unknown>,
    }));
  }
  const teachers = byId.get('tuAswp.teachersTrained');
  if (teachers) {
    emptyData.teachers = (teachers.series ?? []).slice(-MAX_QUARTERS_CHART).map((point) => ({
      quarter: String(point.quarter),
      teachersTrained: asNumber(point.teachersTrained),
      qoq: (point.qoq ?? {}) as Record<string, unknown>,
      yoy: (point.yoy ?? {}) as Record<string, unknown>,
    }));
  }

  emptyData.goalsByMetric = collectGoalsByMetric(section.metrics);
  return emptyData;
}

export function TuAswpPerformanceMetrics() {
  const [mode, setMode] = useState<Mode>('race');
  const { selectedGroup } = useGroup();
  const { data, error, isLoading } = useGroupQuery(
    ['dashboard-metrics', 'tu-aswp'],
    () => dashboardApi.getDashboardMetrics(selectedGroup || 'TU - ASWP'),
    { enabled: selectedGroup === 'TU - ASWP' }
  );

  const tuAswpData = useMemo(() => (data ? mapApiToTuAswpData(data) : null), [data]);
  const hasApiData = tuAswpData !== null;

  return (
    <Stack spacing="lg">
      {error ? (
        <Alert color="yellow" variant="light">
          Unable to load dashboard API data.
        </Alert>
      ) : null}
      {!isLoading && !hasApiData ? (
        <Alert color="yellow" variant="light">
          No dashboard API data returned for this section.
        </Alert>
      ) : null}
      {isLoading && !hasApiData ? (
        <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 980, cols: 1 }]}>
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
        </SimpleGrid>
      ) : null}
      {hasApiData && tuAswpData ? (
        <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 980, cols: 1 }]}>
          <DeclaredCard data={tuAswpData} />
          <GraduatesCard data={tuAswpData} />
          <HighSchoolCard data={tuAswpData} />
          <TeachersCard data={tuAswpData} />
          <ParticipationCompletionCard data={tuAswpData} view="participation" mode={mode} setMode={setMode} />
          <ParticipationCompletionCard data={tuAswpData} view="completion" mode={mode} setMode={setMode} />
        </SimpleGrid>
      ) : null}
    </Stack>
  );
}
